import { useContext } from 'react'
import find from 'lodash/find'
import dayjs from 'dayjs'
import AuthContext from '../../contexts/authContext'
import FuelContext from '../../contexts/fuelContext'
import useApiCallService from '../useApiCallService'
import { FUEL_ID, FUEL_TYPE, MOP_ID, VERSION } from '../../constants'
import { transformTCFAIC, alwaysFourDigit, transformProductIdWiseFpList, } from '../../utills'
import customParseFormat from 'dayjs/plugin/customParseFormat'
dayjs.extend(customParseFormat)

function useFuelService() {
	const {
		authState: {
			userId, LogInTime, DOMIP,
			objDeviceSave: { Msg: guid } = {},
			objSupervisorLogin: { Circle_Response: { org: storeId } = {} } = {},
			objGetStore: { POSID } = {},
			objGetTenderListResp: { TenderListAndroid } = {},
		},
	} = useContext(AuthContext)

	const {
		fuelState: {
			currentTxn: {
				fuelinvoiceData: { TaxInvoice, TxnID, totalAmt, totalVol, ListTax, saleOk, txnStatus, TxnStartTime, } = {},
				fuelInputes: { pumpValue, nozzleValue, mopValue, fleetCard, fleetMode, fleetPrompts, vehicleNo, selectedType, mobileNo, clmVoucherNo, transRefNo, processingCode, rewardMeter = { points: null, mobile: '' }, } = {},
				tcfaICRes = {}, currentTxnPrice, TempID,
			} = {},
		} = {},
		setFuelInvoiceData,
	} = useContext(FuelContext)

	const apiCall = useApiCallService()

	const fetchProductWiseFpList = async ({ fuelType, hideError = false }) => {
		try {
			const Request = {
				Product: fuelType,
				UserID: userId,
				StoreID: storeId,
				GUID: guid,
			}

			let res = {}
			if (process.env.REACT_APP_ENV === 'development') {
				if (fuelType === 'MS') {
					res = { "ErrorCode": "00", "ErrorMsg": "SUCCESS", "lstFpIds": [{ "FpId": "3", "Action": "Quick Mode", "NozzleId": "1" }, { "FpId": "3", "Action": "Quick Mode", "NozzleId": "2" }] }
				} else {
					res = { "ErrorCode": "00", "ErrorMsg": "SUCCESS", "lstFpIds": [{ "FpId": "4", "Action": "Quick Mode", "NozzleId": "1" }] }
				}
			} else {
				res = await apiCall('ProductWiseFpList', Request, { hideError })
			}

			return transformProductIdWiseFpList(res)
		} catch (error) {
			return Promise.reject(error)
		}
	}

	const fetchFpStatusAll = async (hideError = false) => {
		const Request = {
			Host: DOMIP,
			PosId: POSID,
			StoreID: storeId,
			GUID: guid,
		}

		if (process.env.REACT_APP_ENV === 'development') {
			return { "ErrorCode": "00", "ErrorMsg": "SUCCESS", "lstFpStatus": [{ "FpId": "3", "SmId": "0", "FpMainState": { "value": "05H", "enum": { "Unconfigured": null, "Closed": null, "Idle": null, "Error": null, "Calling": null, "PreAuthorized": "05H", "Starting": null, "Starting_paused": null, "Starting_terminated": null, "Fuelling": null, "Fuelling_paused": null, "Fuelling_terminated": null, "Unavailable": null, "Unavailable_and_calling": null } }, "FpSubStates": { "value": 4, "bits": { "IsLockedByPos": 0, "IsSupervised": 0, "IsOnline": 4, "IsEstopped": 0, "HasFreeBuffer": 0, "IsInErrorState": 0, "HasActiveGrades": 0, "IsPreset": 0 } }, "FpSupplStatusPars": { "FuellingDataVol_e": "0000002000", "FuellingDataMon_e": "0000200000" }, "FcGradeId": "20" }, { "FpId": "4", "SmId": "0", "FpMainState": { "value": "05H", "enum": { "Unconfigured": null, "Closed": null, "Idle": null, "Error": null, "Calling": null, "PreAuthorized": "05H", "Starting": null, "Starting_paused": null, "Starting_terminated": null, "Fuelling": null, "Fuelling_paused": null, "Fuelling_terminated": null, "Unavailable": null, "Unavailable_and_calling": null } }, "FpSubStates": { "value": 4, "bits": { "IsLockedByPos": 0, "IsSupervised": 0, "IsOnline": 4, "IsEstopped": 0, "HasFreeBuffer": 0, "IsInErrorState": 0, "HasActiveGrades": 0, "IsPreset": 0 } }, "FpSupplStatusPars": { "FuellingDataVol_e": "0000002259", "FuellingDataMon_e": "0000214605" }, "FcGradeId": "10" }, { "FpId": "5", "SmId": "0", "FpMainState": { "value": "01H", "enum": { "Unconfigured": null, "Closed": "01H", "Idle": null, "Error": null, "Calling": null, "PreAuthorized": null, "Starting": null, "Starting_paused": null, "Starting_terminated": null, "Fuelling": null, "Fuelling_paused": null, "Fuelling_terminated": null, "Unavailable": null, "Unavailable_and_calling": null } }, "FpSubStates": { "value": 0, "bits": { "IsLockedByPos": 0, "IsSupervised": 0, "IsOnline": 0, "IsEstopped": 0, "HasFreeBuffer": 0, "IsInErrorState": 0, "HasActiveGrades": 0, "IsPreset": 0 } }, "FpSupplStatusPars": { "FuellingDataVol_e": "0000000000", "FuellingDataMon_e": "0000000000" }, "FcGradeId": "20" }] }
		}

		return await apiCall(
			`${process.env.REACT_APP_IPC_HOST}/DOMSMicroservices/api/v1.0/FpStatusAll`, Request, { hideError }, true
		)
	}

	const fetchGetItemMrpDetailsRPOS = async ({ prodId }) => {
		const Request = {
			ProductID: prodId,
			StoreID: storeId,
			guId: guid,
		}

		return await apiCall('GetItemMrpDetailsRPOS', Request)
	}

	const checkBinSeriesTCFA = async () => {
		const Request = {
			CARD_NO: fleetCard,
			StoreID: storeId,
			GUID: guid,
		}

		return await apiCall('CheckBinSeriesTCFA', Request)
	}

	const tcfaIC = async (aType = null) => {
		const Request = {
			CARD: `${fleetCard},${fleetMode}`,
			INVN: dayjs().format('YYMMDDHHmmss'),
			StoreID: storeId,
			GUID: guid,
			...(aType !== null ? { aType } : {})
		}

		const icRes = await apiCall('Tcfa_IC', Request)
		const transformedData = transformTCFAIC(icRes.TCFA_Response)
		return {
			...icRes,
			transformed: transformedData.resObj,
			prompts: transformedData.prompt,
		}
	}

	const tcfaPC = async (fuelType = 'MS', aType = null) => {
		let pcResponse = tcfaICRes.TCFA_Response
		if (fleetPrompts.ODRD) {
			const promptOdrd = 'ODRD:' + tcfaICRes.transformed.ODRD
			const inputOdrd = 'ODRD:' + fleetPrompts.ODRD
			pcResponse = pcResponse.replace(promptOdrd, inputOdrd)
		}
		if (vehicleNo !== '') {
			const promptVno = 'VNO:' + tcfaICRes.transformed.VNO
			const inputVno = 'VNO:' + vehicleNo
			pcResponse = pcResponse.replace(promptVno, inputVno)
		}

		const Request = {
			CARD: `${tcfaICRes.transformed.CARD},${fleetMode}`,
			INVN: tcfaICRes.transformed.INVN,
			PCMsg: pcResponse,
			product: FUEL_TYPE[fuelType],
			Qty: totalVol,
			Amount: parseFloat(totalAmt).toFixed(2),
			ProductRate: currentTxnPrice,
			Mobile: fleetMode === 'M' ? tcfaICRes.transformed.MOB : '',
			MobileOTP: fleetMode === 'M' ? `POTP:${fleetPrompts.POTP}` : '',
			InvoiceNo: TaxInvoice,
			StoreID: storeId,
			GUID: guid,
			...(aType !== null ? { aType } : {})
		}

		try {
			return await apiCall('TCFA_PC', Request, { hideError: true })
		} catch (err) {
			if (err?.ErrorMsg?.toLowerCase().includes('transaction already captured for the given invoice')) {
				const { TxnID, BillOfSupply, TaxInvoice, ARV, SecurityDepositReceipt, } = await getTransactionId()
				setFuelInvoiceData({ TxnID, BillOfSupply, TaxInvoice, ARV, SecurityDepositReceipt })
				return await apiCall('TCFA_PC', { ...Request, InvoiceNo: TaxInvoice })
			}

			throw err
		}
	}

	const authorizeTopUpFuelling = async ({ identifier }) => {
		const Request = {
			Host: DOMIP,
			PosId: POSID,
			FpId: pumpValue,
			NozzleId: nozzleValue,
			StoreID: storeId,
			GUID: guid,
			Identifier: identifier,
		}

		return await apiCall(
			`${process.env.REACT_APP_IPC_HOST}/DOMSMicroservices/api/v1.0/Authorize`, Request
		)
	}

	const authorizeFuelling = async ({ fuelBy, fuelByValue, identifier }) => {
		const Request = {
			Host: DOMIP,
			PosId: POSID,
			FpId: pumpValue, //"02"
			NozzleId: nozzleValue,
			PresetType: fuelBy, //Eg. Volume/Money
			PresetLimit: fuelByValue, //"100"
			StoreID: storeId,
			GUID: guid,
			Identifier: identifier,
		}

		if (process.env.REACT_APP_ENV === 'development') {
			return { "Json": null, "ErrorCode": "00", "ErrorMsg": "Success" }
		}

		return await apiCall(
			`${process.env.REACT_APP_IPC_HOST}/DOMSMicroservices/api/v1.0/AuthorizePreset`, Request
		)
	}

	const fuelFpStatus = async ({ pumpValue }, hideError = false) => {
		const Request = {
			Host: DOMIP,
			PosId: POSID,
			FpId: pumpValue,
			StoreID: storeId,
			GUID: guid,
		}

		if (process.env.REACT_APP_ENV === 'development') {
			return { "ErrorCode": "00", "ErrorMsg": "SUCCESS", "FpStatus": { "FpId": "3", "SmId": "0", "FpMainState": { "value": "05H", "enum": { "Unconfigured": null, "Closed": null, "Idle": null, "Error": null, "Calling": null, "PreAuthorized": "05H", "Starting": null, "Starting_paused": null, "Starting_terminated": null, "Fuelling": null, "Fuelling_paused": null, "Fuelling_terminated": null, "Unavailable": null, "Unavailable_and_calling": null } }, "FpSubStates": { "value": 4, "bits": { "IsLockedByPos": 0, "IsSupervised": 0, "IsOnline": 4, "IsEstopped": 0, "HasFreeBuffer": 0, "IsInErrorState": 0, "HasActiveGrades": 0, "IsPreset": 0 } }, "FpSupplStatusPars": { "FuellingDataVol_e": "0000002000", "FuellingDataMon_e": "0000200000" }, "FcGradeId": "20" } }
		}

		return await apiCall(
			`${process.env.REACT_APP_IPC_HOST}/DOMSMicroservices/api/v1.0/FpStatus`, Request, { hideError }, true
		)
	}

	const fuelFpLock = async ({ pumpValue }) => {
		const Request = {
			Host: DOMIP,
			PosId: POSID,
			FpId: pumpValue, //"02"
			StoreID: storeId,
			GUID: guid,
		}

		return await apiCall(
			`${process.env.REACT_APP_IPC_HOST}/DOMSMicroservices/api/v1.0/Lock`, Request
		)
	}

	const saleOkFp = async ({ pumpValue }) => {
		const Request = {
			Host: DOMIP,
			PosId: POSID,
			FpId: pumpValue, //"02"
			StoreID: storeId,
			GUID: guid,
		}

		return await apiCall(
			`${process.env.REACT_APP_IPC_HOST}/DOMSMicroservices/api/v1.0/SaleOK`, Request
		)
	}

	const getTransactionId = async () => {
		const Request = {
			PosId: POSID,
			StoreID: storeId,
			GUID: guid,
		}

		return await apiCall('GetTransactionId', Request)
	}

	const getTaxSummary = async (fuelType, totalAmt, totalVol) => {
		const Request = {
			guId: guid,
			strXmlProduct: [
				{
					seq_id: '1',
					item_id: FUEL_ID[fuelType],
					Storenumber: storeId,
					OrgPerUnitPrice: currentTxnPrice,
					sellingprice: totalAmt,
					qty: totalVol,
				},
			],
		}

		return await apiCall('CalculateTaxSummary', Request)
	}

	const getSaleOkApi = async (cartId) => {
		const req = {
			UserID: userId,
			CartID: cartId,
			GUID: guid,
			StoreID: storeId,
		}

		return await apiCall('GetECOMFpQuickModeTxns', req)
	}

	const updateInvoiceDetails = async (request) => {
		return await apiCall('UpdateFieldsinExistJson', request)
	}

	const updateMOPApi = async request => {
		return await apiCall('ChangeMOPExistJSON', request)
	}

	const uploadTxnData = async (data) => {
		const dmy = dayjs().format('DDMMYYYY')
		const request = {
			Guid: guid,
			StoreNo: storeId,
			Toast: '',
			TxnInfo: {
				PromoDiscountCouponDetails: [],
				PromoList: [],
				TxnHeader: {
					"TxnId": data.TxnHeader.TxnId, "DeviceId": `${storeId}${POSID}`, "TxnStartTime": data.TxnHeader.TxnStartTime,
					"TxnEndTime": data.TxnHeader.TxnEndTime,
					"PaymentStartTime": data.TxnHeader.TxnStartTime,
					"PaymentEndTime": data.TxnHeader.TxnEndTime,
					"TxnTotal": data.TxnHeader.TxnTotal,
					"TaxInvoice": data.TxnHeader.TaxInvoice,
					ReceiptRefID: `${storeId}${POSID}${alwaysFourDigit(data.TxnHeader.TxnId)}${dmy}`,
					ReceiptTextPath: `C:\\RPOSStoreReceipt\\${dmy}\\${storeId}${POSID}${alwaysFourDigit(data.TxnHeader.TxnId)}${dmy}.txt`,
					"ProductVersion": VERSION, "LogonTime": LogInTime, "UserId": userId,
					"Area": "", "BalanceLoyaltyPoints": "0.0", "BillOfSupply": "", "BlockNo": "", "BuildingName": "", "ChangeDue": "0.00", "City": "", "ContactNumber": "", "CouponID": "", "CouponType": "", "CouponValue": "0.00", "CouponValueParam": "", "CouponValueType": "", "CurReferenceID": "1", "CustomerID": "", "CustomerName": "", "DoctorsAdd1": "", "DoctorsAdd2": "", "DoctorsAdd3": "", "DoctorsName": "", "ESOrderID": "", "EmailID": "", "EmailId": "", "FlatNo": "", "FloorNo": "", "IDProofCaptureStatus": "", "IsAddressCaptured": "", "IsCouponApplied": "", "IsIDProofRequiredFlag": "", "IsLoyaltyCardCaptured": "", "IsLoyaltyRedeemed": "", "IsValidLoyaltyRedemption": "", "IsVatExtra": "false", "ItemCount": "1", "LoyaltyCardNumber": "", "MNP_MSISDN": "", "OrderType": "", "PanCardNo": "", "PatientsAdd1": "", "PatientsAdd2": "", "PatientsAdd3": "", "PatientsName": "", "Pincode": "", "PlotNo": "", "PromoTotal": "0.00", "RedeemAmount": "", "RefundTxnSupervisorID": "", "RoundOffAmount": "0.00", "RoundOffConfigValue": "0.50", "Sector": "", "SocietyName": "", "State": "", "Street": "", "TaxableAmount": "0.00", "TransactionType": "SALE", "TxnAppliedDiscValue": "0.00", "TxnCouponSupervisorID": "", "TxnDiscAppliedTime": "", "TxnDiscSupervisorID": "", "TxnDiscValue": "", "TxnDiscValueFlag": "", "TxnMarkDownReason": "", "TxnMarkDownReasonDesc": "", "TxnSalesManID": "", "VoidTxnSupervisorID": "", "isMDTApplied": "", "Supply_State_Code": "27", "TxnStatus": "COMPLETED",
				},
				TxnItemList: [{
					"TxnId": data.TxnHeader.TxnId, "DeviceId": `${storeId}${POSID}`, "TxnStartTime": data.TxnHeader.TxnStartTime,
					"ScanTime": data.TxnHeader.TxnEndTime,
					"ProductID": FUEL_ID[data.TxnHeader.fuelType],
					"Quantity": data.TxnHeader.Quantity,
					"OrgPrice": data.TxnTenderList.currentTxnPrice,
					"EffectivePrice": data.TxnHeader.TxnTotal,
					"ItemSellingPrice": data.TxnHeader.TxnTotal,
					"ItemUnitPriceAfterDiscount": data.TxnHeader.TxnTotal,
					"OriginalPriceWithQty": data.TxnHeader.TxnTotal,
					"SellingPrice": data.TxnHeader.TxnTotal,
					"TaxDetails": {
						"TaxCodeType": "TAX_TYPE_INCL",
						"TaxType": "TAX_TYPE_PERCENTAGE",
						"TaxableAmount": data.TxnTenderList.ListTax.TaxableAmount,
						"TaxAmount": data.TxnTenderList.ListTax.TaxAmount,
						"TaxRate": data.TxnTenderList.ListTax.TaxRate,
						"TaxId": data.TxnTenderList.ListTax.TaxId,
						"TaxChar": data.TxnTenderList.ListTax.TaxChar,
						"TaxDesc": data.TxnTenderList.ListTax.TaxDesc,
					},
					"BatchNumber": "", "Colorcode": "0", "CouponID": "", "CouponSupervisorID": "", "CouponType": "", "CouponValue": "", "CouponValueParam": "", "CouponValueType": "", "DeliveryLocation": "", "DeliveryLocationId": "", "Description": "", "DiscountAmount": "0.00", "DiscountID": "", "DiscountPercentage": "0.00", "DiscountWithQty": "0.00", "EmbeddedTotalPrice": "0.00", "EnterEANorSKU": "", "EntryMethod": "S", "ExpectedDeliveryDate": "", "ExpectedDeliveryTime": "", "ExpiryDate": "", "Extnd_bckt_nbr": "0", "Extnd_prom_nbr": "0", "HSN_CODE": "", "IsCouponApplied": "0", "ItemDiscAppliedTime": "", "ItemEmpDiscSupervisorID": "", "ItemEmployeeDiscount": "0.00", "ItemMarkDownDescription": "", "ItemMarkDownDiscountFlag": "", "ItemMarkDownDiscountParam": "0.00", "ItemMarkDownDiscountValue": "0.00", "ItemMarkDownReason": "", "ItemMarkDownSupervisorID": "", "ItemMesurementType": "MEASURE_TYPE_QUANTITY", "ItemType": "TYPE_GRAB_AND_GO", "LnkItemList": [], "LocationType": "", "MDIAppliedDateTime": "", "MD_FG": "0", "MaximumQuantity": "1", "OrgSeqId": "", "OriginalBarcode": "", "OtherDiscounts": "0.00", "ParentReferenceID": "", "PosMsgDetails": [], "QtyChangeDateTime": "", "QuantityAdded": "", "QuantityFlag": "0", "SalesManID": "", "SequenceID": "1", "Status": "", "StockCheckRequired": "0", "StockCount": "", "StoreID": "", "Stylecode": "0", "SupervisorID": "", "TxnItemPromoDetails": [], "UnitOfMeasure": "MEASURE_TYPE_QUANTITY", "ValidatedPosMsgId": "", "VoidSupervisorID": "", "VoidedDateTime": "", "isDiscountApplied": "0", "isLinkedItemsPresentFlag": "0", "isMDIApplied": "0", "isPromotionAvailableFlag": "0", "isTradeInItem": "0", "isVoidAllowed": "0", "itm_point": "0", "prom_nbr": "0",
				}],
				TxnFuelPointList: [{
					"TxnId": data.TxnHeader.TxnId, "DeviceId": `${storeId}${POSID}`, "TxnStartTime": data.TxnHeader.TxnStartTime,
					"ITEMID": FUEL_ID[data.TxnHeader.fuelType],
					"FPID": data.TxnHeader.FpId,
					"FpGradeOptionNo": data.TxnFuelPointList.saleOk.FpGradeOptionNo,
					"TempID": data.TxnFuelPointList.saleOk.TempId,
					"DOMSTransSeqNo": data.TxnFuelPointList.saleOk.TransSeqNo,
					"StartTotalizer": data.TxnFuelPointList.saleOk.StartTotalizer,
					"EndTotalizer": data.TxnFuelPointList.saleOk.EndTotalizer,
					"FuelStartTime": data.TxnFuelPointList.saleOk.FuelStartTime,
					"FuelEndTime": data.TxnFuelPointList.saleOk.FuelEndTime,
					"ITEMSEQUENCENO": "1", "TXNMODE": "SEMI_QUICKMODE", "TXNMODEREMARKS": "", "VEHICLE_NO": "",
				}],
				TxnTenderList: [{
					"TxnId": data.TxnHeader.TxnId, "DeviceId": `${storeId}${POSID}`, "TxnStartTime": data.TxnHeader.TxnStartTime,
					"UserId": userId,
					"Amount": data.TxnHeader.TxnTotal,
					"Type": data.TxnTenderList.Type,
					"MOPID": data.TxnTenderList.MOPID,
					"Description": data.TxnTenderList.Description,
					"ReceiptTypeDescription": data.TxnTenderList.ReceiptTypeDescription,
					"SequenceId_Tender": 1, "Status": "SUCCESS", "TenderMode": "DEBIT", "CardNumber": "", "ApprovalCode": "", "ChequeNo": "", "GVNo": "", "AuthCode": "", "ExpiryDate": "", "FirstName": "", "LastName": "", "ProcessingCode": "", "LoanRefNo": "", "TenderNumber": "", "TypeDescription": "", "RILTenderName": "", "SchemeNo": "", "TenderName": "", "CustMobNo": "", "ApprovCode": "", "CustName": "", "AccorVoucher": "", "Last4Digits": "", "CustID": "", "TransRefNo": "", "XmlGeneratedFlag": "",
				}]
			},
		}

		return await apiCall('UploadTxnData', request)
	}

	const uploadTxn = async ({
		fuelType, TxnEndTime, cardNo, authCode, cartid, orderType, newSaleOk = {}, FPId, TempId, taxDetails = {},
		rewardMeterPoints = null,
	}) => {
		const dtstring = dayjs().format('DDMMYYYY')
		const mopInfo = find(TenderListAndroid, { Tender_ID: `${mopValue}` })
		const txnId = taxDetails.TxnID || TxnID
		const taxInvoice = taxDetails.TaxInvoice || TaxInvoice

		const Request = {
			Guid: guid,
			StoreNo: storeId,
			Toast: '',
			TxnInfo: {
				PromoDiscountCouponDetails: [],
				PromoList: [],
				TxnHeader: {
					TxnId: txnId,
					DeviceId: `${storeId}${POSID}`,
					LogonTime: LogInTime,
					UserId: userId,
					ContactNumber: mobileNo,
					TxnTotal: totalAmt,
					TxnStartTime: newSaleOk.FuelStartTime || saleOk.FuelStartTime,
					TxnEndTime: newSaleOk.FuelEndTime || saleOk.FuelEndTime,
					ESOrderID: /*  navigation.getParam('ORN') */ cartid,
					OrderType: orderType,
					PaymentEndTime: TxnEndTime,
					PaymentStartTime: newSaleOk.FuelStartTime || saleOk.FuelStartTime,
					ReceiptRefID: `${storeId}${POSID}${alwaysFourDigit(txnId)}${dtstring}`,
					ReceiptTextPath: `C:\\RPOSStoreReceipt\\${dtstring}\\${storeId}${POSID}${alwaysFourDigit(txnId)}${dtstring}.txt`,
					TaxInvoice: taxInvoice,
					TxnStatus: txnStatus || 'COMPLETED',
					BalanceLoyaltyPoints: rewardMeter.points !== null ? (rewardMeterPoints || rewardMeter.points) : '0.0',
					LoyaltyCardNumber: rewardMeter?.mobile || '',
					Area: '', BillOfSupply: '', BlockNo: '', BuildingName: '', ChangeDue: '0.00', City: '', CouponID: '', CouponType: '', CouponValue: '0.00', CouponValueParam: '', CouponValueType: '', CurReferenceID: '1', CustomerID: '', CustomerName: '', DoctorsAdd1: '', DoctorsAdd2: '', DoctorsAdd3: '', DoctorsName: '', EmailID: '', EmailId: '', FlatNo: '', FloorNo: '', IDProofCaptureStatus: '', IsAddressCaptured: '', IsCouponApplied: '', IsIDProofRequiredFlag: '', IsLoyaltyCardCaptured: '', IsLoyaltyRedeemed: '', IsValidLoyaltyRedemption: '', IsVatExtra: 'false', ItemCount: '1',  MNP_MSISDN: '', PanCardNo: '', PatientsAdd1: '', PatientsAdd2: '', PatientsAdd3: '', PatientsName: '', Pincode: '', PlotNo: '', ProductVersion: VERSION, PromoTotal: '0.00', RedeemAmount: '', RefundTxnSupervisorID: '', RoundOffAmount: '0.00', RoundOffConfigValue: '0.50', Sector: '', SocietyName: '', State: '', Street: '', Supply_State_Code: '27', TaxableAmount: '0.00', TransactionType: 'SALE', TxnAppliedDiscValue: '0.00', TxnCouponSupervisorID: '', TxnDiscAppliedTime: '', TxnDiscSupervisorID: '', TxnDiscValue: '', TxnDiscValueFlag: '', TxnMarkDownReason: '', TxnMarkDownReasonDesc: '', TxnSalesManID: '', VoidTxnSupervisorID: '', isMDTApplied: '',
				},
				TxnItemList: [
					{
						TxnId: txnId,
						TxnStartTime: newSaleOk.FuelStartTime || saleOk.FuelStartTime,
						DeviceId: `${storeId}${POSID}`,
						Quantity: totalVol,
						ScanTime: TxnEndTime,
						SellingPrice: totalAmt,
						EffectivePrice: totalAmt,
						ItemSellingPrice: totalAmt,
						ItemUnitPriceAfterDiscount: totalAmt,
						OrgPrice: currentTxnPrice,
						OriginalPriceWithQty: totalAmt,
						ProductID: FUEL_ID[fuelType],
						TaxDetails: {
							TaxCodeType: 'TAX_TYPE_INCL',
							TaxType: 'TAX_TYPE_PERCENTAGE',
							TaxableAmount: ListTax?.[0]?.Net_Amount,
							TaxAmount: ListTax?.[0]?.Tax_Amount,
							TaxRate: ListTax?.[0]?.Tax_Percent,
							TaxId: ListTax?.[0]?.MainTax_Id,
							TaxChar: ListTax?.[0]?.Tax_Char,
							TaxDesc: ListTax?.[0]?.Tax_Desc,
						},
						ItemMesurementType: 'MEASURE_TYPE_QUANTITY', ItemType: 'TYPE_GRAB_AND_GO', UnitOfMeasure: 'MEASURE_TYPE_QUANTITY', BatchNumber: '', Colorcode: '0', CouponID: '', CouponSupervisorID: '', CouponType: '', CouponValue: '', CouponValueParam: '', CouponValueType: '', DeliveryLocation: '', DeliveryLocationId: '', Description: '', DiscountAmount: '0.00', DiscountID: '', DiscountPercentage: '0.00', DiscountWithQty: '0.00', EmbeddedTotalPrice: '0.00', EnterEANorSKU: '', EntryMethod: 'S', ExpectedDeliveryDate: '', ExpectedDeliveryTime: '', ExpiryDate: '', Extnd_bckt_nbr: '0', Extnd_prom_nbr: '0', HSN_CODE: '', IsCouponApplied: '0', ItemDiscAppliedTime: '', ItemEmpDiscSupervisorID: '', ItemEmployeeDiscount: '0.00', ItemMarkDownDescription: '', ItemMarkDownDiscountFlag: '', ItemMarkDownDiscountParam: '0.00', ItemMarkDownDiscountValue: '0.00', ItemMarkDownReason: '', ItemMarkDownSupervisorID: '',			 MaximumQuantity: '1', LnkItemList: [], LocationType: '', MDIAppliedDateTime: '', MD_FG: '0', OrgSeqId: '', OriginalBarcode: '', OtherDiscounts: '0.00', ParentReferenceID: '', PosMsgDetails: [], QtyChangeDateTime: '', QuantityAdded: '', QuantityFlag: '0', SalesManID: '', SequenceID: '1', Status: '', StockCheckRequired: '0', StockCount: '', StoreID: '', Stylecode: '0', SupervisorID: '', TxnItemPromoDetails: [], ValidatedPosMsgId: '', VoidSupervisorID: '', VoidedDateTime: '', isDiscountApplied: '0', isLinkedItemsPresentFlag: '0', isMDIApplied: '0', isPromotionAvailableFlag: '0', isTradeInItem: '0', isVoidAllowed: '0', itm_point: '0', prom_nbr: '0',
					},
				],
				TxnFuelPointList: [
					{
						TxnId: txnId,
						TxnStartTime: newSaleOk.FuelStartTime || saleOk.FuelStartTime,
						DeviceId: `${storeId}${POSID}`,
						ITEMSEQUENCENO: '1',
						ITEMID: FUEL_ID[fuelType],
						FPID: FPId || pumpValue,
						VEHICLE_NO: vehicleNo,
						DOMSTransSeqNo: newSaleOk.TransSeqNo || saleOk.TransSeqNo,
						StartTotalizer: newSaleOk.StartTotalizer || saleOk.StartTotalizer,
						EndTotalizer: newSaleOk.EndTotalizer || saleOk.EndTotalizer,
						FuelStartTime: newSaleOk.FuelStartTime || saleOk.FuelStartTime,
						FuelEndTime: newSaleOk.FuelEndTime || saleOk.FuelEndTime,
						FpGradeOptionNo: newSaleOk.FpGradeOptionNo || saleOk.FpGradeOptionNo,
						TXNMODEREMARKS: '',
						TXNMODE: 'SEMI_QUICKMODE',
						TempID: TempId || TempID,
					},
				],
			},
		}

		if (mopValue === '2') {
			Request.TxnInfo.TxnTenderList = [
				{
					Type: 'TENDERTYPE_CC',
					CardNumber: cardNo,
					ApprovalCode: '',
					Status: 'SUCCESS',
					ChequeNo: '',
					GVNo: '',
					Amount: totalAmt,
					Description: mopInfo ? mopInfo.Tender_Name : '',
					AuthCode: authCode,
					ExpiryDate: '',
					MOPID: '2',
					FirstName: '',
					LastName: '',
					ProcessingCode: '',
					LoanRefNo: '',
					ReceiptTypeDescription: `CR/DR xxxxxxxxxxxx${cardNo}`,
					TenderNumber: '',
					TypeDescription: '',
					RILTenderName: '',
					TenderMode: 'DEBIT',
					SchemeNo: '',
					TenderName: '',
					CustMobNo: '',
					ApprovCode: '',
					CustName: '',
					TxnId: txnId,
					DeviceId: `${storeId}${POSID}`,
					TxnStartTime: newSaleOk.FuelStartTime || saleOk.FuelStartTime,
					UserId: userId,
					AccorVoucher: '',
					Last4Digits: '',
					CustID: '',
					TransRefNo: '',
					SequenceId_Tender: 1,
					XmlGeneratedFlag: '',
				},
			]
		} else if (mopValue === '27') {
			Request.TxnInfo.TxnTenderList = [
				{
					Type: 'TENDERTYPE_ONLINE_EFT',
					CardNumber: fleetMode === 'M' ? cardNo : fleetCard,
					fleetMode: fleetMode,
					ApprovalCode: authCode,
					Status: 'SUCCESS',
					ChequeNo: fleetCard,
					GVNo: '',
					Amount: totalAmt,
					Description: mopInfo ? mopInfo.Tender_Name : '',
					AuthCode: authCode,
					ExpiryDate: '',
					MOPID: mopValue,
					FirstName: '',
					LastName: '',
					ProcessingCode: '',
					LoanRefNo: '',
					ReceiptTypeDescription: `TCFA ${fleetCard}`,
					TenderNumber: '',
					TypeDescription: fleetMode,
					RILTenderName: '',
					TenderMode: 'DEBIT',
					SchemeNo: '',
					TenderName: '',
					CustMobNo: tcfaICRes.transformed.MOB,
					ApprovCode: tcfaICRes.transformed.ISN,
					CustName: '',
					TxnId: txnId,
					DeviceId: `${storeId}${POSID}`,
					TxnStartTime: newSaleOk.FuelStartTime || saleOk.FuelStartTime,
					UserId: userId,
					AccorVoucher: '',
					Last4Digits: '',
					CustID: '',
					TransRefNo: tcfaICRes.transformed.INVN,
					SequenceId_Tender: 1,
					XmlGeneratedFlag: '',
				},
			]
		} else if ([MOP_ID['clmVoucher'], MOP_ID['voucherRedemption']].includes(parseInt(mopValue, 10))) {
			Request.TxnInfo.TxnTenderList = [{
				"Type": "TENDERTYPE_ONLINE_EFT",
				"CardNumber": clmVoucherNo,
				"ApprovalCode": "",
				"Status": "SUCCESS",
				"ChequeNo": "",
				"GVNo": "",
				"Amount": totalAmt,
				"Description": "VOUCHER REDEMPTION",
				"AuthCode": "",
				"ExpiryDate": "",
				"MOPID": mopValue,
				"FirstName": "",
				"LastName": "",
				"ProcessingCode": "",
				"LoanRefNo": "",
				"ReceiptTypeDescription": "VOUCHER REDEMPTION ",
				"TenderNumber": "",
				"TypeDescription": "",
				"RILTenderName": "",
				"TenderMode": "DEBIT",
				"SchemeNo": "",
				"TenderName": "",
				"CustMobNo": mobileNo,
				"ApprovCode": "",
				"CustName": "",
				"TxnId": txnId,
				"DeviceId": `${storeId}${POSID}`,
				"TxnStartTime": newSaleOk.FuelStartTime || saleOk.FuelStartTime,
				"UserId": userId,
				"AccorVoucher": "",
				"Last4Digits": "",
				"CustID": "",
				"TransRefNo": "",
				"SequenceId_Tender": 1,
				"XmlGeneratedFlag": ""
			}]
		} else if ([MOP_ID['paytm'], MOP_ID['offlinePaytm']].includes(parseInt(mopValue, 10))) {
			Request.TxnInfo.TxnTenderList = [{
				"GVNo": "", "ChequeNo": "", "ExpiryDate": "", "FirstName": "", "LastName": "", "LoanRefNo": "", "TenderNumber": "", "TypeDescription": "", "RILTenderName": "", "SchemeNo": "", "TenderName": "", "CustMobNo": "", "AccorVoucher": "", "Last4Digits": "", "XmlGeneratedFlag": "", "ApprovCode": "", "CustName": "", "CustID": "",
				"Type": "TENDERTYPE_ONLINE",
				"CardNumber": cardNo,
				"ApprovalCode": authCode,
				"Status": "SUCCESS",
				"Amount": totalAmt,
				"Description": mopInfo?.Tender_Name || "PAYTM",
				"AuthCode": authCode,
				"MOPID": `${mopValue}`,
				"ProcessingCode": processingCode,
				"ReceiptTypeDescription": `${mopInfo?.Tender_Name || 'PAYTM'} ${cardNo}`,
				"TenderMode": "DEBIT",
				"TxnId": txnId,
				"DeviceId": `${storeId}${POSID}`,
				"TxnStartTime": newSaleOk.FuelStartTime || saleOk.FuelStartTime,
				"UserId": userId,
				"TransRefNo": transRefNo,
				"SequenceId_Tender": 1,
			}]
		} else {
			Request.TxnInfo.TxnTenderList = [
				{
					Type: `TENDERTYPE_${mopInfo ? mopInfo.Tender_Name.toUpperCase() : ''
						}`,
					CardNumber: '',
					ApprovalCode: '',
					Status: 'SUCCESS',
					ChequeNo: '',
					GVNo: '',
					Amount: totalAmt,
					Description: mopInfo ? mopInfo.Tender_Name : '',
					AuthCode: '',
					ExpiryDate: '',
					MOPID: mopValue,
					FirstName: '',
					LastName: '',
					ProcessingCode: '',
					LoanRefNo: '',
					ReceiptTypeDescription: mopInfo
						? mopInfo.Tender_Name.toUpperCase()
						: '',
					TenderNumber: '',
					TypeDescription: '',
					RILTenderName: '',
					TenderMode: 'DEBIT',
					SchemeNo: '',
					TenderName: '',
					CustMobNo: '',
					ApprovCode: '',
					CustName: '',
					TxnId: txnId,
					DeviceId: `${storeId}${POSID}`,
					TxnStartTime: newSaleOk.FuelStartTime || saleOk.FuelStartTime,
					UserId: userId,
					AccorVoucher: '',
					Last4Digits: '',
					CustID: '',
					TransRefNo: '',
					SequenceId_Tender: 1,
					XmlGeneratedFlag: '',
				},
			]
		}

		return await apiCall('UploadTxnData', Request)
	}

	const getOrderByVehicle = async ({ VehicleNumber }) => {
		const Request = {
			GUID: guid,
			VehicleNumber: VehicleNumber,
		}

		return await apiCall(`OrderDetailByVehicleNo`, Request)
	}

	const getOrderByStore = () => {
		const Request = {
			StoreID: storeId,
			DeviceID: storeId + POSID,
			GUID: guid,
		}

		return apiCall(`GetEcomOrdersCompleted`, Request)
	}

	const getOrderStatus = async cartId => {
		const Request = {
			StoreID: storeId,
			GUID: guid,
			CartId: cartId,
		}

		return await apiCall(`CheckEcomOrderStatus`, Request)
	}

	const createOfflineOrder = async fuelByValue => {
		const Request = {
			DeviceID: storeId + POSID,
			UserID: userId,
			StoreID: storeId,
			GUID: guid,
			orderList: [
				{
					orderedQuantity: fuelByValue,
					productCodeForVehicleType: selectedType, //mdu or pfc
					OrderedRatePerUnit: currentTxnPrice,
					mop: 'PAY_AT_RO', statusCode: 'E0001', unit: 'L', PPAgencyName: 'RPOS_OFFLINE', authenticationType: '', authenticationNo: '', pnocROId: '', customerName: '', assetAddress: '', assetTag: '', rfid: '', vehicleRegistrationNo: '', crN_No: '', SystemName: '', mesg: '', PPEcomId: '', PPRdelightId: '',
				},
			],
		}

		return await apiCall(`EcomOrderOfflineCreation`, Request)
	}

	const ecomDelivaredQtyInsert = async (fuelByValue, cartId) => {
		const Request = {
			cartId: cartId,
			DeliveredQty: fuelByValue,
			StoreID: storeId,
			GUID: guid,
		}

		return await apiCall(`EcomOrderDeliveryQtyInsert`, Request)
	}


	const filledAtRORevisedQty = async (cartid) => {
		const Request = {
			"CartId": cartid,
			"CurretRate": currentTxnPrice,
			"TxnId": TxnID,
			"DeviceId": `${storeId}${POSID}`,
			"TxnStartTime": dayjs().format('DD/MM/YYYY HH:mm:ss'),
			"StoreID": storeId,
			"GUID": guid,
		}

		return await apiCall(`FilledAtRORevisedQty`, Request)
	}

	const updateFilledCancelledStatus = async (cartid) => {
		const isSAPOrder = mopValue === `${MOP_ID['sap']}`
		const Request = {
			"CartId": cartid,
			"TxnId": TxnID,
			"DeviceId": `${storeId}${POSID}`,
			"TxnStartTime": TxnStartTime,
			"StoreID": storeId,
			"GUID": guid,
			"APIFLag": isSAPOrder ? 'JIO' : 'ECOM',
			"FilledOrderDtls": {
				"InvoiceNo": TaxInvoice,
				"InvoiceDate": dayjs(TxnStartTime, 'DD/MM/YYYY HH:mm:ss').format('DD-MM-YYYY HH:mm:ss'),
				"DeliveredQuantity": isSAPOrder ? totalVol / 1000 : totalVol,
				"DeliveredRate": parseFloat(currentTxnPrice).toFixed(2),
				"DeliveredAmount": parseFloat(totalAmt).toFixed(2),
				"FilledAtRODt": "",
				"Fleet_MOP_Auth_Code": "0",
				"IrsAmount": 0.0
			},
			"CancelOrderDtls": null,
		}

		return await apiCall(`UpdateFilledCancelledStatus`, Request)
	}

	const fetchQuickModePendingTxnList = async () => {
		const Request = {
			UserID: userId,
			GUID: guid,
			StoreID: storeId,
		}

		return await apiCall('GetFpQuickModeTxnByUserId', Request, { hideError: true, })
	}

	const fetchQuickModeTxnDetails = async ({ pumpValue, selectedType }) => {
		const Request = {
			FpID: pumpValue,
			StoreID: storeId,
			GradeName: selectedType,
			GUID: guid,
		}

		return await apiCall('GetFpQuickModeTxnDetails', Request, { hideError: true, })
	}

	const saveInvoiceApi = async (receiptText, TxnId) => {
		const dmy = dayjs().format('DDMMYYYY')
		const req = {
			"OrderType": "", "DelType": "", "OrderID": "", "typeOfBill": "", "mobileNo": "", "taxInvoiceNumber": "",
			StoreID_In: storeId,
			receiptText,
			posID: POSID,
			strTnx: TxnId,
			ReceiptPath: `C:\\RPOSStoreReceipt\\${dmy}\\${storeId}${POSID}${alwaysFourDigit(TxnId)}${dmy}.txt`,
			guId: guid,
		}

		return apiCall('PrintOffline_SaveFile_REST', req)
	}

	return {
		fetchProductWiseFpList,
		fetchFpStatusAll,
		fetchGetItemMrpDetailsRPOS,
		checkBinSeriesTCFA,
		tcfaIC,
		tcfaPC,
		authorizeTopUpFuelling,
		authorizeFuelling,
		fuelFpStatus,
		fuelFpLock,
		saleOkFp,
		getTransactionId,
		getTaxSummary,
		getSaleOkApi,
		uploadTxn, uploadTxnData,
		updateFilledCancelledStatus,
		filledAtRORevisedQty,
		getOrderByVehicle,
		getOrderStatus,
		fetchQuickModePendingTxnList,
		fetchQuickModeTxnDetails,
		createOfflineOrder,
		ecomDelivaredQtyInsert,
		getOrderByStore,
		updateInvoiceDetails,
		updateMOPApi,
		saveInvoiceApi,
	}
}

export default useFuelService
